/**
 * Brazilian Portuguese message catalog — mirrors the shape of
 * `messages.en.ts` exactly; `satisfies MessagesShape` makes a missing or
 * renamed key a TypeScript error. Register: você (locked in
 * l10n/locales.md).
 *
 * Platform terms follow pt-BR UI conventions (configurações, pareamento
 * for pairing, espelhamento for mirroring — Android-pt-BR's own screen-
 * mirroring term, dispensar for dismiss). "Glance" is the untranslated
 * feature brand name (glossary D1); "clipe" renders the D2 loanword in
 * Portuguese orthography.
 *
 * Plural notes: pt-BR cardinal categories are {one, many, other} with
 * `many` millions-only — `selectPlural` calls supply {one, other}.
 * Portuguese `one` covers BOTH 0 and 1, so "0 resultado" renders
 * singular; counts are never hardcoded in the `one` form.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Conectado",
    statusReconnecting: "Reconectando…",
    statusDisconnected: "Desconectado",
    openSettings: "Abrir configurações",
    pairedFallbackDevice: "seu telefone",
    publicModeToggleEnable: "Ocultar conteúdo das notificações",
    publicModeToggleDisable: "Mostrar conteúdo das notificações",
  },

  notifications: {
    replyAriaLabel: "Responder",
    replyPlaceholder: "Responder…",
    replySend: "Enviar",
    sent: "Enviado",
    failedToSend: "Falha ao enviar",
    unread: "Não lida",
    dismiss: "Dispensar",
    notificationImageAlt: "Imagem da notificação",
  },

  conversations: {
    replyAriaLabel: "Responder",
    replyPlaceholder: "Responder…",
    replySend: "Enviar",
    sent: "Enviado",
    failedToSend: "Falha ao enviar",
    dismiss: "Dispensar conversa",
    messagesListLabel: "Mensagens desta conversa",
    deviceOwner: "Você",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Nenhuma mensagem não lida"
        : `${count} ${selectPlural("pt-BR", count, {
            one: "mensagem não lida",
            other: "mensagens não lidas",
          })}`,
  },

  list: {
    today: "Hoje",
    yesterday: "Ontem",
    searchNotifications: "Pesquisar notificações",
    clearSearch: "Limpar pesquisa",
    clearAll: "Dispensar todas",
    markAllRead: "Marcar tudo como lido",
    groupCheckboxLabel: "Agrupar",
    noNotificationsTitle: "Nenhuma notificação ainda",
    noNotificationsDesc: "As novas mensagens espelhadas do seu telefone aparecerão aqui.",
    noMatchesTitle: "Nenhum resultado",
    notificationsList: "Notificações",
    privacyAppList: "Notificações por app",
    clear: "Dispensar",
    clearMatches: (count: number): string =>
      `Dispensar ${count} ${selectPlural("pt-BR", count, {
        one: "resultado",
        other: "resultados",
      })}`,
    noMatchesDesc: (query: string): string => `Nenhuma notificação corresponde a "${query}".`,
    appNotificationsGroup: (appName: string): string => `Notificações de ${appName}`,
    dismissAllFrom: (appName: string): string => `Dispensar todas de ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("pt-BR", count, { one: "mensagem", other: "mensagens" })
          : kind === "calls"
            ? selectPlural("pt-BR", count, { one: "chamada", other: "chamadas" })
            : selectPlural("pt-BR", count, { one: "notificação", other: "notificações" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "dispensar",
    kbdNavigate: "navegar",
    kbdOrSeparator: "ou",
  },

  settings: {
    back: "Voltar às notificações",
    settings: "Configurações",
    appearance: "Aparência",
    theme: "Tema",
    themeAuto: "Automático",
    themeLight: "Claro",
    themeDark: "Escuro",
    language: "Idioma",
    languageSystemDefault: "Padrão do sistema",
    notificationsSection: "Notificações",
    osNotifications: "Notificações do sistema",
    callNotifications: "Notificações de chamada",
    privacySection: "Privacidade",
    analyticsToggle: "Estatísticas de uso anônimas",
    analyticsToggleDescription: "Apenas contagens e marcos — nunca suas notificações.",
    connection: "Conexão",
    status: "Status",
    statusConnected: "Conectado",
    statusReconnecting: "Reconectando…",
    statusDisconnected: "Desconectado",
    disconnectFromPhone: "Desconectar do telefone",
    disconnectConfirm: "Tem certeza? Isso vai desparear seu dispositivo.",
    yesDisconnect: "Sim, desconectar",
    cancel: "Cancelar",
    aboutAndSupport: "Sobre e suporte",
    version: "Versão",
    encryption: "Criptografia",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Precisa de ajuda? Escreva para __EMAIL__",
    disconnectFailed: "Falha ao desconectar — tente de novo",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Desconectado, mas a limpeza local falhou: ${errorMsg}. Recarregue a página.`,
    disconnectLocalCleanupFailed: "Desconectado, mas a limpeza local falhou. Recarregue a página.",
  },

  pairing: {
    enterCodeInstruction: "Digite este código no app Reflecto",
    pairedSuccessfully: "Pareado com sucesso!",
    pairingFailed: "Falha no pareamento",
    requestingPairingCode: "Solicitando código de pareamento…",
    codeExpired: "Código expirado",
    tryAgain: "Tentar de novo",
    failedToStartPairing: "Não foi possível iniciar o pareamento",
    needAppText: "Precisa do app para Android? Escreva para __EMAIL__ para obter acesso.",
  },

  dnd: {
    title: "Não perturbe",
    description: "Pause as notificações neste dispositivo pela próxima hora.",
    snooze1h: "Adiar por 1 hora",
    snoozeActiveUntil: (formattedTime: string): string => `Ativo até ${formattedTime}`,
    cancelSnooze: "Cancelar",
  },

  connectors: {
    title: "Serviços conectados",
    emptyBody:
      "Nenhum serviço conectado ainda. Conecte o ChatGPT ou o Claude nas configurações de conector deles e aprove o código no seu telefone.",
    scopeSend: "Pode enviar notificações",
    added: (date: string): string => `Adicionado ${date}`,
    lastUsed: (relative: string): string => `Último uso ${relative}`,
    neverUsed: "Nunca usado",
    revoke: "Desconectar",
    revokeConfirm: (label: string): string =>
      `Desconectar ${label}? Ele perderá o acesso imediatamente.`,
    revokeAction: "Desconectar",
    revokeConfirmLabel: (label: string): string => `Desconectar ${label}`,
    cancel: "Cancelar",
    loadError: "Não foi possível carregar os serviços conectados. Tente de novo.",
    revokeError: "Não foi possível desconectar o serviço. Tente de novo.",
    retry: "Tentar de novo",
  },

  feedback: {
    title: "Enviar feedback",
    close: "Fechar",
    messageLabel: "Sua mensagem",
    messagePlaceholder: "Achou um bug, tem uma ideia ou algo parece estranho? Conte para a gente.",
    categoryLabel: "Categoria",
    categoryBug: "Bug",
    categoryIdea: "Ideia",
    categoryOther: "Outro",
    emailLabel: "E-mail (opcional, apenas se quiser uma resposta)",
    emailPlaceholder: "voce@exemplo.com.br",
    emailHelp: "Não vamos adicionar você a nenhuma lista.",
    disclosure:
      "Sua mensagem é enviada ao servidor do Reflecto e registrada no nosso rastreador interno de tarefas. Anexamos a versão do app e um identificador de dispositivo atribuído no pareamento para correlacionar os relatos. Não vemos suas notificações, contatos nem nenhum dado pessoal. O e-mail é opcional — informe apenas se quiser uma resposta. Não vamos adicionar você a nenhuma lista.",
    submit: "Enviar feedback",
    sending: "Enviando…",
    success: "Obrigado — lido por uma pessoa, geralmente em até 24 h",
    error: "Não foi possível enviar. Tente de novo.",
    done: "Concluído",
    sendFeedback: "Enviar feedback",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Fechar",
    aiSummaryTag: "Resumo de IA",
    errorAi: "Não foi possível gerar um resumo agora.",
    retryCta: "Tentar de novo",
    downloadHeading: "Receba resumos de IA",
    downloadBody:
      "Ponha a leitura em dia com um resumo de IA gerado no aparelho. Tudo roda no seu dispositivo.",
    downloadCta: "Baixar modelo",
    downloadPrivacyNote: "Um download único. O servidor nunca vê suas notificações.",
    downloadingHeading: "Baixando modelo…",
    caughtUp: "Você está em dia — nada precisa de você agora.",
    needsYou: "Requer atenção",
    quiet: "O restante",
    scanning: (pending: number): string => `Resumindo ${pending}…`,
    jump: "Abrir notificação",
    affordanceReply: "Responder",
    affordanceOpen: "Abrir",
    affordanceCopyOtp: "Copiar código",
    buttonLabel: "Glance",
  },

  update: {
    title: "Atualização disponível",
    description: "Uma nova versão do Reflecto está pronta. Recarregue para atualizar.",
    reload: "Recarregar",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Demos a este dispositivo o nome "${name}".`,
    cardLooksGood: "Ficou bom",
    cardEdit: "Editar",
    cardAriaLabel: "Confirmar nome do dispositivo",
    renameTitle: "Renomear este dispositivo",
    renameLabel: "Nome do dispositivo",
    renameSave: "Salvar",
    renameCancel: "Cancelar",
    renameFailed: "Não foi possível renomear. Tente de novo.",
  },

  collision: {
    title: (name: string): string => `Você já tem um "${name}"`,
    subtitle: "Como devemos chamar este?",
    chipWork: (name: string): string => `Trabalho ${name}`,
    chipHome: (name: string): string => `Casa ${name}`,
    chipPersonal: (name: string): string => `Pessoal ${name}`,
    customLabel: "Nome personalizado",
    save: "Salvar",
    useAnyway: "Usar o nome original mesmo assim",
    saveFailed: "Não foi possível salvar o novo nome. Tente de novo.",
    ariaLabel: "Resolver nome de dispositivo duplicado",
  },

  clipboard: {
    title: "Área de transferência",
    empty: "Os clipes dos seus outros dispositivos aparecem aqui. Toque para copiar.",
    from: (label: string): string => `de ${label}`,
    copy: "Copiar para a área de transferência",
    copied: "Copiado",
    reveal: "Mostrar",
    hide: "Ocultar",
    sensitiveChip: "Sensível",
    cloudChip: "via seu token de API",
    maskedHint: 'Conteúdo oculto — toque em "Mostrar" para ver',
    sendPlaceholder: "Cole o texto para enviar…",
    sendCta: "Enviar",
    sendSensitiveToggle: "Marcar como sensível",
    sendSuccess: (n: number): string =>
      `Enviado para ${n} ${selectPlural("pt-BR", n, {
        one: "dispositivo",
        other: "dispositivos",
      })}.`,
    sendPartial: (n: number, total: number): string =>
      `Enviado para ${n} de ${total} ${selectPlural("pt-BR", total, {
        one: "dispositivo",
        other: "dispositivos",
      })}.`,
    sendNoDevices: "Nenhum dispositivo pareado para receber.",
    sendFailed: "Não foi possível enviar. Tente de novo.",
    sendEmpty: "Nada para enviar.",
    dockExpand: "Mostrar histórico de clipes",
    dockCollapse: "Recolher",
    dockSendTitle: "Enviar para seus dispositivos",
    dockFromClipboard: (chars: number): string =>
      `Da área de transferência · ${chars} ${selectPlural("pt-BR", chars, {
        one: "caractere",
        other: "caracteres",
      })}`,
    dockSend: (n: number): string => `Enviar para ${n}`,
    dockCancel: "Cancelar",
    dockRecentHeading: "Recentes",
    dockEmptyClipboard: "Sua área de transferência está vazia.",
    shareTitle: "Enviar para seus dispositivos",
    shareReview:
      "Revise o que você está enviando. O conteúdo vai para seus dispositivos pareados, criptografado de ponta a ponta.",
    shareSend: "Enviar",
    shareCancel: "Cancelar",
    shareNotPaired: "Pareie um dispositivo primeiro para enviar texto compartilhado.",
    shareOpenApp: "Abrir o Reflecto",
  },
  install: {
    title: "Instalar Reflecto",
    body: "Adicione à sua tela inicial para um acesso mais rápido.",
    cta: "Instalar",
    dismissAria: "Dispensar o aviso de instalação",
  },
  common: {
    loading: "Carregando",
  },
  errors: {
    notPaired: "Não pareado",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} no ${os}`,
    webFallback: "Navegador web",
  },
  palette: {
    placeholder: "Digite um comando…",
    noResults: "Nenhum comando correspondente",
    ariaLabel: "Paleta de comandos",
    clearAll: "Dispensar todas as notificações",
    glance: "Resumo do Glance",
    cancelSnooze: "Cancelar pausa",
    copyClip: "Copiar o último clipe recebido",
  },
} satisfies MessagesShape;
